<template>
  <div class="news-page">
    <van-nav-bar :title="$t('q38')" class="nav-bar" fixed placeholder :border="false">
      <template #left>
        <img src="/img/newLogin/download.png" @click="back()" class="back-icon"/>
      </template>
    </van-nav-bar>

    <div class="news-content">
      <van-pull-refresh v-model="isLoading" @refresh="onRefresh">
        <van-empty
          v-if="normalizedNotice.length === 0"
          :description="$t('ui.notice.empty')"
          class="news-empty"
        />

        <section v-else class="news-article-list">
          <article v-for="item in normalizedNotice" :key="item.id" class="news-article">
            <header class="news-article__header">
              <div class="news-article__eyebrow">{{ item.displayTime }}</div>
              <h1 class="news-article__title">{{ item.title }}</h1>
            </header>

            <div class="news-article__body">
              <template v-for="(block, index) in item.blocks">
                <h2 v-if="block.type === 'heading'" :key="`${item.id}-heading-${index}`" class="news-heading">
                  {{ block.text }}
                </h2>
                <p v-else-if="block.type === 'paragraph'" :key="`${item.id}-paragraph-${index}`" class="news-paragraph">
                  {{ block.text }}
                </p>
                <div v-else-if="block.type === 'bullet'" :key="`${item.id}-bullet-${index}`" class="news-bullet">
                  <span class="news-bullet__dot"></span>
                  <span>{{ block.text }}</span>
                </div>
              </template>
            </div>
          </article>
        </section>
      </van-pull-refresh>
    </div>
  </div>

</template>

<script>
export default {
  data() {
    return {
      isLoading: false,
      notice: []
    };
  },
  computed: {
    normalizedNotice() {
      const source = Array.isArray(this.notice)
        ? this.notice
        : this.notice && typeof this.notice === 'object'
          ? Object.values(this.notice)
          : [];

      return source.map((item, index) => {
        const safeItem = item && typeof item === 'object' ? item : {};
        const content = String(safeItem.text || '').trim() || this.$t('ui.notice.no_details');
        const rawBlocks = this.normalizeContentBlocks(content);
        const fallbackTitle = this.extractPrimaryTitle(rawBlocks);
        const title = this.normalizeNoticeTitle(safeItem.name, fallbackTitle);
        return {
          id: safeItem.id || `${safeItem.create_time || 'news'}-${index}`,
          title,
          content,
          blocks: this.removeDuplicatedHeading(rawBlocks, title),
          displayTime: this.formatNoticeTime(safeItem.create_time)
        };
      });
    }
  },
  methods: {
    back(){
      return window.history.back();
    },
    formatNoticeTime(value) {
      const raw = String(value || '').trim();
      if (!raw) {
        return '--';
      }
      return raw.length > 16 ? raw.slice(0, 16) : raw;
    },
    normalizeNoticeTitle(value, fallbackTitle) {
      const rawTitle = String(value || '').trim();
      const lowerTitle = rawTitle.toLowerCase();
      const ignoredTitles = [
        '日语', '日本', '日文', 'jp', 'ja', 'japan', 'japanese',
        '中文', '汉语', 'zh', 'cn', 'chinese',
        '英文', '英语', 'en', 'english',
        '韩语', '韩文', 'kr', 'ko', 'korean'
      ];

      if (!rawTitle || ignoredTitles.includes(lowerTitle) || ignoredTitles.includes(rawTitle)) {
        return fallbackTitle || this.$t('ui.notice.fallback_title');
      }

      return rawTitle;
    },
    extractPrimaryTitle(blocks) {
      const firstHeading = (blocks || []).find((block) => block && block.type === 'heading' && block.text);
      if (firstHeading) {
        return firstHeading.text;
      }
      const firstParagraph = (blocks || []).find((block) => block && block.type === 'paragraph' && block.text);
      return firstParagraph ? firstParagraph.text.slice(0, 42) : '';
    },
    normalizeComparableText(value) {
      return String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/[：:;；,.，。!！?？"'“”‘’、]/g, '')
        .trim()
        .toLowerCase();
    },
    removeDuplicatedHeading(blocks, title) {
      if (!Array.isArray(blocks) || blocks.length === 0) {
        return [];
      }

      const normalizedTitle = this.normalizeComparableText(title);
      if (!normalizedTitle) {
        return blocks;
      }

      return blocks.filter((block, index) => {
        if (index !== 0 || !block || block.type !== 'heading') {
          return true;
        }
        return this.normalizeComparableText(block.text) !== normalizedTitle;
      });
    },
    normalizeContentBlocks(content) {
      const lines = String(content || '')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length === 0) {
        return [{ type: 'paragraph', text: this.$t('ui.notice.no_details') }];
      }

      return lines.map((line, index) => {
        if (/^(?:\d+\.)\s+/.test(line)) {
          return { type: 'heading', text: line };
        }
        if (/^(?:[-*•])\s+/.test(line)) {
          return { type: 'bullet', text: line.replace(/^(?:[-*•])\s+/, '') };
        }
        if (index === 0 && line.length <= 42) {
          return { type: 'heading', text: line };
        }
        return { type: 'paragraph', text: line };
      });
    },
    getNoticeList(){
      this.isLoading = true;
      this.$http({
        method: 'get',
        url: 'sys_get_notice_list'
      }).then(res=>{
        this.notice = Array.isArray(res.data) ? res.data : (res.data || []);
      }).catch(() => {
        this.$toast(this.$t('ui.notice.toast_load_fail'));
      }).finally(() => {
        this.isLoading = false;
      });
    },
    onRefresh() {
      this.getNoticeList();
    }
  },
  created() {
    this.getNoticeList();
  }
};
</script>

<style lang='less' scoped>
@import "../../assets/css/base.css";
.news-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(107, 59, 74, 0.36), transparent 35%),
    #090909;
}

::v-deep .nav-bar .van-nav-bar,
::v-deep .nav-bar .van-nav-bar__content {
  background: rgba(13, 13, 13, 0.96);
}

::v-deep .nav-bar .van-nav-bar__title {
  color: #f8d2bf;
  font-size: 32px;
  font-weight: 700;
}

.back-icon {
  width: 20px;
  height: 15px;
}

.news-content {
  min-height: calc(100vh - 46px);
  padding: 18px 16px 36px;
}

.news-article-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.news-article {
  padding: 24px 18px 28px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(18, 18, 18, 0.98), rgba(9, 9, 9, 0.98));
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.news-article__header {
  margin-bottom: 18px;
}

.news-article__eyebrow {
  color: rgba(255, 255, 255, 0.56);
  font-size: 18px;
  line-height: 1.2;
}

.news-article__title {
  margin: 10px 0 0;
  color: #ffffff;
  font-size: 32px;
  line-height: 1.32;
  font-weight: 700;
}

.news-empty {
  margin-top: 80px;
}

.news-article__body {
  color: rgba(255, 255, 255, 0.86);
}

.news-heading {
  margin: 20px 0 10px;
  color: #ffffff;
  font-size: 25px;
  line-height: 1.45;
  font-weight: 700;
}

.news-heading:first-child {
  margin-top: 0;
}

.news-paragraph {
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 22px;
  line-height: 1.72;
  word-break: break-word;
}

.news-bullet {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 22px;
  line-height: 1.72;
}

.news-bullet__dot {
  width: 8px;
  height: 8px;
  margin-top: 14px;
  border-radius: 50%;
  background: #ffffff;
  flex: 0 0 8px;
}

::v-deep .news-empty .van-empty__description,
::v-deep .van-pull-refresh__track .van-pull-refresh__head *,
::v-deep .van-loading__text {
  color: #f3c7a2;
  font-size: 24px;
}
</style>
